<script setup lang="ts">

import { usePresentation } from "@presentation/Types";

const presentation = usePresentation();
const model = defineModel<number>( { required: true });
const { count, size = 'md' } = defineProps<{ count: number, size: 'sm' | 'md' | 'lg' }>();

const dotSizes: Record<string,string> = { sm: 'w-2 h-2', md: 'w-2.5 h-2.5', lg: 'w-3 h-3' };
const dotSize = dotSizes[ size ];

</script>

<template>

    <div class="flex items-center gap-4 h-10 2xl:scale-110 2xl:right-2">

        <!-- RESET THE CURRENT PAGE ANIMATIONS TO THE BEGINNING -->
        <button 
            v-show="presentation.triggers > 0 && presentation.currentTrigger >= 0 && presentation.isTransitioning == false"
            title="Restart the animations"
            @click="() => presentation.currentTrigger = -1"
            class="hover:brightness-110 hover:bg-white/[4%] rounded-full w-11 h-11 flex items-center justify-center hover:shadow">
            <Icon name="reset" />
        </button>

        <div v-if="presentation.isTitlePage == false && ( count > 1 || presentation.triggers > 0 ) && presentation.isTransitioning == false"
            class="mx-auto flex items-center justify-center gap-x-1 rounded-full shadow-lg p-1"
            :style="{ 
                background: `linear-gradient( to bottom, rgb( from ${presentation.theme.navColorFrom} r g b / 93%), rgb( from ${presentation.theme.navColorTo} r g b / 100%) )`,
                border: presentation.theme.navBorderColor == null ? undefined : `solid 2px ${presentation.theme.navBorderColor}`
            }">

            <template v-for="index in count">
                <a v-if="index > ( presentation.currentSection.hasTitlePage ?  1 : 0 )"
                    class="group rounded-full flex gap-x-1.5 p-1 transition duration-300 ease cursor-pointer"
                    @click="() => presentation.goto( presentation.active, index - 1)">
                    <div class="pointer-events-none w-32 absolute -top-20 -left-14 -mt-1 
                                transition-all duration-300 flex justify-center
                                opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100">
                        <img 
                            :src="presentation.currentSection.pages[ index - 1 ].screenshot" 
                            class="rounded-lg shadow-xl ring-1 ring-gray-900"
                            :onerror="`this.src = '/images/${presentation.key}/pending.jpg'`" 
                        />
                        <div class="absolute -bottom-2 pointer-events-none
                            inline-block w-0 h-0 border-solid border-t-[6px] border-r-[5px] border-l-[5px] border-b-0 border-l-transparent border-r-transparent border-t-gray-900 border-b-transparent" 
                        />
                    </div>
                    <div class="rounded-full bg-white/30 ring-1 ring-white/10"
                        :class="[{ 
                            '!bg-white !ring-white': index == model + 1,
                        }, dotSize ]">
                    </div>
                    <transition mode="out-in"
                            enter-active-class="transition ease-out duration-150"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-150"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95">

                        <div v-if="index == model + 1 && presentation.triggers > 0" class="flex items-center justify-center gap-x-1.5 ml-1">
                            <a v-for="index in presentation.triggers"
                                class="rounded-full w-2 h-2 ring-1 ring-emerald-500/50"
                                :class="{ 
                                    '!bg-emerald-500 !ring-emerald-500 pointer-events-none': index <= presentation.currentTrigger + 1 
                                }">
                            </a>
                        </div>
                    </transition>
                </a>
            </template>
        </div>

    </div>

</template>